import React, { useMemo } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkMath from 'remark-math';
import rehypeKatex from 'rehype-katex';
import 'katex/dist/katex.min.css';
import './StreamingMarkdown.css';

interface PartialMarkdownRendererProps {
  content: string;
  isFromAI?: boolean;
}

/**
 * Специальный рендерер для частичного маркдауна в потоковом режиме.
 * Обрабатывает незавершенные списки и другие элементы.
 */
export const PartialMarkdownRenderer: React.FC<PartialMarkdownRendererProps> = ({ 
  content, 
  isFromAI = false 
}) => {
  
  // Препроцессинг LaTeX для совместимости с remark-math
  const preprocessLaTeX = (content: string): string => {
    return content
      // Блочная математика: \[...\] -> $$...$$
      .replace(/\\\[(.*?)\\\]/gs, (_, eq) => `$$${eq}$$`)
      // Инлайн математика: \(...\) -> $...$
      .replace(/\\\((.*?)\\\)/gs, (_, eq) => `$${eq}$`);
  };

  // Обработка контента для улучшенного стриминга
  const preprocessPartialMarkdown = useMemo(() => {
    if (!content) return '';
    
    let processedContent = content;
    
    // Сначала обрабатываем LaTeX
    processedContent = preprocessLaTeX(processedContent);
    
    // Обрабатываем неполные блоки кода - добавляем закрывающие backticks если их нет
    const codeBlockMatches = processedContent.match(/```[\s\S]*$/);
    if (codeBlockMatches && !processedContent.endsWith('```')) {
      const lastCodeBlock = codeBlockMatches[0];
      const backtickCount = (lastCodeBlock.match(/```/g) || []).length;
      if (backtickCount % 2 === 1) {
        // Неполный блок кода - не добавляем закрывающие backticks пока стрим не завершен
        // Просто оставляем как есть для лучшего UX
      }
    }
    
    // Обрабатываем неполные инлайн коды
    const inlineCodeMatches = processedContent.match(/`[^`]*$/);
    if (inlineCodeMatches && !processedContent.match(/`[^`]*`$/)) {
      // Неполный инлайн код - оставляем как есть
    }
    
    // Добавляем переносы строк перед заголовками (только если нет переноса)
    processedContent = processedContent.replace(/(^|[^\n])(#+\s)/g, '$1\n\n$2');
    
    // Добавляем переносы строк перед списками только если нужно
    processedContent = processedContent.replace(/([^\n])([-*+]\s)/gm, '$1\n$2');
    processedContent = processedContent.replace(/([^\n])(\d+\.\s)/gm, '$1\n$2');
    
    // Добавляем переносы строк перед блоками кода
    processedContent = processedContent.replace(/(^|[^\n])(```)/g, '$1\n\n$2');
    
    // Добавляем переносы строк перед блочной математикой  
    processedContent = processedContent.replace(/(^|[^\n])(\$\$)/g, '$1\n\n$2');
    
    // Обрабатываем форматирование текста - добавляем пробелы если их нет
    processedContent = processedContent.replace(/(\S)(\*\*[^*]+\*\*)(\S)/g, '$1 $2 $3');
    processedContent = processedContent.replace(/(\S)(\*[^*]+\*)(\S)/g, '$1 $2 $3');
    processedContent = processedContent.replace(/(\S)(\_\_[^_]+\_\_)(\S)/g, '$1 $2 $3');
    processedContent = processedContent.replace(/(\S)(\_[^_]+\_)(\S)/g, '$1 $2 $3');
    
    // Обрабатываем инлайн код
    processedContent = processedContent.replace(/(\S)(`[^`]+`)(\S)/g, '$1 $2 $3');
    
    // Обрабатываем инлайн математику
    processedContent = processedContent.replace(/(\S)(\$[^$]+\$)(\S)/g, '$1 $2 $3');
    
    // Убираем лишние пустые строки в начале
    processedContent = processedContent.replace(/^\n+/, '');
    
    // Убираем более 2 переносов подряд
    processedContent = processedContent.replace(/\n{3,}/g, '\n\n');
    
    return processedContent;
  }, [content]);

  // Компоненты для рендеринга
  const components = useMemo(() => ({
    ul: ({ children, ...props }: any) => (
      <ul 
        {...props} 
        className="list-disc list-outside pl-6 mb-4 space-y-1"
      >
        {children}
      </ul>
    ),
    ol: ({ children, ...props }: any) => (
      <ol 
        {...props} 
        className="list-decimal list-outside pl-6 mb-4 space-y-1"
      >
        {children}
      </ol>
    ),
    li: ({ children, ...props }: any) => (
      <li 
        {...props} 
        className="leading-relaxed"
      >
        {children}
      </li>
    ),
    p: ({ children, ...props }: any) => {
      // Обрабатываем переносы строк в параграфах
      if (typeof children === 'string') {
        const lines = children.split('\n').filter(line => line.trim() !== '');
        if (lines.length > 1) {
          return (
            <div {...props} className="mb-2 last:mb-0 leading-relaxed">
              {lines.map((line, index) => (
                <div key={index}>{line}</div>
              ))}
            </div>
          );
        }
      }
      
      return (
        <p {...props} className="mb-2 last:mb-0 leading-relaxed">
          {children}
        </p>
      );
    },
    code: ({ inline, className, children, ...props }: any) => {
      const isCodeBlock = className && className.startsWith('language-');
      
      // Если это инлайн код, отображаем как инлайн
      if (inline || !isCodeBlock) {
        return (
          <code 
            {...props} 
            className={`px-1.5 py-0.5 rounded text-sm font-mono ${
              isFromAI 
                ? 'bg-gray-200 dark:bg-gray-600 text-gray-800 dark:text-gray-200' 
                : 'bg-blue-700 text-blue-100'
            }`}
          >
            {children}
          </code>
        );
      }
      
      // Только для настоящих блоков кода с языком создаем pre
      return (
        <pre className={`my-3 p-4 rounded-lg overflow-x-auto border font-mono text-sm leading-relaxed ${
          isFromAI 
            ? 'bg-gray-100 dark:bg-gray-800 border-gray-200 dark:border-gray-700 text-gray-800 dark:text-gray-200' 
            : 'bg-blue-700 border-blue-600 text-blue-100'
        }`}>
          <code className={className || ''}>
            {children}
          </code>
        </pre>
      );
    },
    h1: ({ children, ...props }: any) => (
      <h1 {...props} className="text-xl font-bold mb-3 mt-4 first:mt-0 border-b pb-2">
        {children}
      </h1>
    ),
    h2: ({ children, ...props }: any) => (
      <h2 {...props} className="text-lg font-bold mb-2 mt-3 first:mt-0">
        {children}
      </h2>
    ),
    h3: ({ children, ...props }: any) => (
      <h3 {...props} className="text-md font-semibold mb-2 mt-3 first:mt-0">
        {children}
      </h3>
    ),
    strong: ({ children, ...props }: any) => (
      <strong {...props} className="font-semibold">
        {children}
      </strong>
    ),
    em: ({ children, ...props }: any) => (
      <em {...props} className="italic">
        {children}
      </em>
    ),
    a: ({ children, href, ...props }: any) => (
      <a 
        {...props} 
        href={href}
        className={`hover:underline transition-colors ${
          isFromAI 
            ? 'text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300' 
            : 'text-blue-200 hover:text-blue-100'
        }`}
        target="_blank"
        rel="noopener noreferrer"
      >
        {children}
      </a>
    ),
    // Блочная математика
    div: ({ className, children, ...props }: any) => {
      if (className === 'math math-display') {
        return (
          <div 
            {...props} 
            className="math-display my-4 text-center overflow-x-auto"
          >
            {children}
          </div>
        );
      }
      return <div {...props} className={className}>{children}</div>;
    },
    // Инлайн математика
    span: ({ className, children, ...props }: any) => {
      if (className === 'math math-inline') {
        return (
          <span 
            {...props} 
            className="math-inline mx-1"
          >
            {children}
          </span>
        );
      }
      return <span {...props} className={className}>{children}</span>;
    },
    // Таблицы
    table: ({ children, ...props }: any) => (
      <div className="overflow-x-auto my-4">
        <table 
          {...props} 
          className="min-w-full border-collapse border border-gray-300 dark:border-gray-600"
        >
          {children}
        </table>
      </div>
    ),
    th: ({ children, ...props }: any) => (
      <th 
        {...props} 
        className="border border-gray-300 dark:border-gray-600 px-4 py-2 bg-gray-100 dark:bg-gray-700 font-semibold text-left"
      >
        {children}
      </th>
    ),
    td: ({ children, ...props }: any) => (
      <td 
        {...props} 
        className="border border-gray-300 dark:border-gray-600 px-4 py-2"
      >
        {children}
      </td>
    ),
    // Цитаты
    blockquote: ({ children, ...props }: any) => (
      <blockquote 
        {...props} 
        className="border-l-4 border-gray-300 dark:border-gray-600 pl-4 my-4 italic text-gray-600 dark:text-gray-400"
      >
        {children}
      </blockquote>
    ),
    // Горизонтальная линия
    hr: ({ ...props }: any) => (
      <hr 
        {...props} 
        className="my-6 border-t border-gray-300 dark:border-gray-600"
      />
    ),

  }), [isFromAI]);


  return (
    <div className={`streaming-markdown max-w-none ${isFromAI ? 'text-gray-900 dark:text-white' : 'text-inherit'}`}>
      <ReactMarkdown 
        remarkPlugins={[remarkGfm, remarkMath]}
        rehypePlugins={[rehypeKatex]}
        components={components}
      >
        {preprocessPartialMarkdown}
      </ReactMarkdown>
    </div>
  );
};